"use client";

import Link from "next/link";
import { useState } from "react";

enum FAQBlockVariantsEnum {
  Default,
  SingularPage,
}

const FAQBlockVariants = {
  [FAQBlockVariantsEnum.Default]:
    "sticky z-10 top-0 bg-white flex items-center justify-between w-full py-5 md:py-6 text-left text-[18px] md:text-[1.25rem] font-medium",
  [FAQBlockVariantsEnum.SingularPage]:
    "flex items-center justify-between w-full p-6 text-2xl font-semibold",
};

const FAQBlockContainerVariants = {
  [FAQBlockVariantsEnum.Default]: "relative border-b last:border-none border-[#e5e7eb]",
  [FAQBlockVariantsEnum.SingularPage]: "border-t border-light-grey",
};

const FAQBlock = ({
  question,
  answer,
  href,
  blockClassName: blockCn,
  additionalNode,
}: FAQProps) => {
  const [isActive, setIsActive] = useState<boolean>(false);

  return (
    <div
      className={`${
        FAQBlockContainerVariants[FAQBlockVariantsEnum.Default]
      } ${blockCn}`}
    >
      {!href ? (
        <>
          <button
            className={FAQBlockVariants[FAQBlockVariantsEnum.Default]}
            type="button"
            aria-expanded={isActive}
            onClick={() => {
              setIsActive(!isActive);
            }}
          >
            <span
              className="max-w-[90%]"
              dangerouslySetInnerHTML={{ __html: question }}
            />
            <span className="text-2xl">{isActive ? "-" : "+"}</span>
          </button>
          <div
            className={`duration-300 transition-transform text-left ${
              isActive ? "pt-1.5 pb-6" : "max-h-0 p-0 overflow-hidden"
            }`}
          >
            <p className="[&>*]>ul:list-disc" dangerouslySetInnerHTML={{ __html: answer }} />
            <div className={!answer ? "" : "mt-2.5"}>{additionalNode}</div>
          </div>
        </>
      ) : (
        <Link
          href={href}
          className={FAQBlockVariants[FAQBlockVariantsEnum.Default]}
        >
          <span className="max-md:max-w-[80%]">{question}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="16"
            viewBox="0 0 18 16"
            fill="black"
            stroke="5"
            className="stroke-1 stroke-black hover:stroke-orange"
          >
            <path
              d="M10.7368 0.742188L9.69079 1.78824L15.1184 7.21587H1V8.69614H15.2171L9.69079 14.2126L10.7368 15.2586L18 8.00535L10.7368 0.742188Z"
              fill="%23414042"
            />
          </svg>
        </Link>
      )}
    </div>
  );
};

export interface FAQProps {
  order: number;
  question: string;
  answer: string;
  additionalNode?: React.ReactNode;
  href?: string;
  blockClassName?: string;
}

interface FAQBlockProps {
  blockName?: string;
  questions: FAQProps[];
  maxQuestionsWithoutExpand?: number;
  headerClassName?: string;
  containerClassName?: string;
  blockClassName?: string;
}

const FAQSection = ({
  blockName,
  questions,
  maxQuestionsWithoutExpand,
  headerClassName = "",
  containerClassName = "",
  blockClassName = "",
}: FAQBlockProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  return (
    <div className={containerClassName}>
      {!!blockName && <h2 className={`${headerClassName}`}>{blockName}</h2>}
      <div>
        {!!maxQuestionsWithoutExpand ? (
          <>
            {questions
              .sort((a, b) => a.order - b.order)
              .map((question, idx) => {
                const isHiddenBeforeExpand =
                  !isExpanded && idx >= maxQuestionsWithoutExpand;
                return (
                  <div
                    key={question.question.toLowerCase().replace(" ", "-")}
                    className={isHiddenBeforeExpand ? "hidden" : ""}
                  >
                    <FAQBlock
                      question={question.question}
                      answer={question.answer}
                      href={question.href}
                      additionalNode={question.additionalNode}
                      order={question.order}
                    />
                  </div>
                );
              })}
          </>
        ) : (
          <>
            {questions
              .sort((a, b) => a.order - b.order)
              .map((question) => (
                <FAQBlock
                  key={question.question.toLowerCase().replace(" ", "-")}
                  question={question.question}
                  answer={question.answer}
                  href={question.href}
                  additionalNode={question.additionalNode}
                  order={question.order}
                />
              ))}
          </>
        )}
      </div>
      {!!maxQuestionsWithoutExpand && (
        <div className="flex justify-center">
          <button
            type="button"
            className="text-orange font-semibold text-xl mt-10 px-6 py-4 flex items-center gap-2"
            onClick={() => {
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? "Collapse" : "Expand for more"}
            {isExpanded ? (
              <svg
                width="29"
                height="29"
                viewBox="0 0 29 29"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="1.5"
                  y="1.33203"
                  width="26"
                  height="26"
                  rx="13"
                  stroke="#F26922"
                  stroke-width="2"
                />
                <path
                  d="M7.94578 15.2868V13.1168H21.4618V15.2868H7.94578Z"
                  fill="#F26922"
                />
              </svg>
            ) : (
              <svg
                width="28"
                height="29"
                viewBox="0 0 28 29"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="1"
                  y="1.33203"
                  width="26"
                  height="26"
                  rx="13"
                  stroke="#F26922"
                  stroke-width="2"
                />
                <path
                  d="M12.9638 21.5028V15.2408H7.16678V13.0708H12.9638V6.80881H15.2268V13.0708H21.0238V15.2408H15.2268V21.5028H12.9638Z"
                  fill="#F26922"
                />
              </svg>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default FAQSection;
